import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import TierModal from '../../components/TierModal';

const LicenseTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tiersRes, appsRes] = await Promise.all([
        api.getLicenseTiers(),
        api.getChesiApps()
      ]);

      if (tiersRes.success) {
        setTiers(tiersRes.data || []);
      }
      if (appsRes.success) {
        setApps(appsRes.data || []);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTier(null);
    setShowModal(true);
  };

  const handleEdit = (tier) => {
    setEditingTier(tier);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this license tier? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.deleteLicenseTier(id);
      if (response.success) {
        loadData();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSave = async (tierData) => {
    try {
      setError('');
      let response;
      if (editingTier) {
        response = await api.updateLicenseTier(editingTier.id, tierData);
      } else {
        response = await api.createLicenseTier(tierData);
      }

      if (response.success) {
        setShowModal(false);
        setEditingTier(null);
        loadData();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleToggleApp = async (tierId, appId, isEnabled) => {
    try {
      const response = await api.setTierAppAccess(tierId, appId, !isEnabled);
      if (response.success) {
        loadData();
      }
    } catch (error) {
      setError(error.message);
    }
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-6">
          <button className="btn btn-primary btn-sm" onClick={handleCreate}>
            <i className="fas fa-plus"></i> New License Tier
          </button>
        </div>
        <div className="col-md-6 text-right">
          <div className="btn-group btn-group-sm">
            <button 
              className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-default'}`}
              onClick={() => setViewMode('table')}
            >
              <i className="fas fa-table"></i> Table
            </button>
            <button 
              className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-default'}`}
              onClick={() => setViewMode('cards')}
            >
              <i className="fas fa-th"></i> Cards
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="card">
          <div className="card-body">
            <table className="table table-bordered table-hover table-sm">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Internal Name</th>
                  <th>Price (Monthly)</th>
                  <th>Max Users</th>
                  <th>Support Level</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tiers.length > 0 ? (
                  tiers.map(tier => (
                    <tr key={tier.id}>
                      <td><code style={{ fontSize: '10px' }}>{tier.tier_code || tier.slug}</code></td>
                      <td>
                        <strong>{tier.name}</strong>
                        {tier.is_popular && <span className="badge bg-warning ml-1" style={{ fontSize: '9px' }}>POPULAR</span>}
                      </td>
                      <td style={{ fontSize: '11px' }}>{tier.internal_name || '-'}</td>
                      <td>
                        <strong>{tier.currency} {parseFloat(tier.price_monthly).toLocaleString()}</strong>
                        {tier.price_yearly && (
                          <div style={{ fontSize: '10px', color: '#6c757d' }}>
                            {tier.currency} {parseFloat(tier.price_yearly).toLocaleString()}/yr
                          </div>
                        )}
                      </td>
                      <td>{tier.max_users === 999 ? 'Unlimited' : tier.max_users}</td>
                      <td>
                        <span className={`badge ${
                          tier.support_level === 'dedicated' ? 'bg-danger' :
                          tier.support_level === 'enterprise' ? 'bg-warning' :
                          tier.support_level === 'priority' ? 'bg-info' :
                          'bg-secondary'
                        }`} style={{ fontSize: '10px' }}>
                          {tier.support_level}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${tier.is_active ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '10px' }}>
                          {tier.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-sm btn-info mr-1" onClick={() => handleEdit(tier)}>
                          <i className="fas fa-edit"></i>
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(tier.id)}>
                          <i className="fas fa-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">No license tiers found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="row">
          {tiers.map(tier => {
            const tierFeatures = typeof tier.features === 'string' 
              ? JSON.parse(tier.features) 
              : tier.features || [];
            const enabledApps = tier.apps?.filter(app => app.is_enabled) || [];
            
            return (
              <div key={tier.id} className="col-md-4 mb-3">
                <div className={`card ${tier.is_popular ? 'border-warning' : ''}`}>
                  {tier.is_popular && (
                    <div className="card-header bg-warning">
                      <strong>POPULAR</strong>
                    </div>
                  )}
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h5 className="card-title mb-0">{tier.name}</h5>
                        {tier.internal_name && (
                          <small className="text-muted" style={{ fontSize: '9px' }}>{tier.internal_name}</small>
                        )}
                        {tier.tier_code && (
                          <div><code style={{ fontSize: '9px' }}>{tier.tier_code}</code></div>
                        )}
                      </div>
                      <span className={`badge ${tier.is_active ? 'bg-success' : 'bg-secondary'}`} style={{ fontSize: '9px' }}>
                        {tier.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-muted" style={{ fontSize: '10px' }}>{tier.description}</p>
                    <div className="mb-2">
                      <strong style={{ fontSize: '18px' }}>{tier.currency} {parseFloat(tier.price_monthly).toLocaleString()}</strong>
                      <span className="text-muted" style={{ fontSize: '11px' }}>/month</span>
                    </div>
                    {tier.price_yearly && (
                      <div className="mb-2">
                        <span style={{ fontSize: '12px' }}>{tier.currency} {parseFloat(tier.price_yearly).toLocaleString()}/year</span>
                      </div>
                    )}
                    <div className="mb-2">
                      <small><strong>Max Users:</strong> {tier.max_users === 999 ? 'Unlimited' : tier.max_users}</small>
                      {tier.max_warehouses > 1 && (
                        <small className="ml-2"><strong>Warehouses:</strong> {tier.max_warehouses}</small>
                      )}
                    </div>
                    <div className="mb-2">
                      <span className={`badge ${
                        tier.support_level === 'dedicated' ? 'bg-danger' :
                        tier.support_level === 'enterprise' ? 'bg-warning' :
                        tier.support_level === 'priority' ? 'bg-info' :
                        'bg-secondary'
                      }`} style={{ fontSize: '9px' }}>
                        {tier.support_level} Support
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <strong style={{ fontSize: '11px' }}>Features:</strong>
                      <ul style={{ fontSize: '10px', marginTop: '4px', paddingLeft: '20px' }}>
                        {tierFeatures.slice(0, 3).map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                        {tierFeatures.length > 3 && (
                          <li style={{ color: '#6c757d' }}>+{tierFeatures.length - 3} more</li>
                        )}
                      </ul>
                    </div>

                    <div className="mb-3">
                      <strong style={{ fontSize: '11px' }}>App Access ({enabledApps.length}):</strong>
                      <div style={{ fontSize: '10px', marginTop: '4px' }}>
                        {apps.map(app => {
                          const tierApp = tier.apps?.find(a => a.id === app.id);
                          const isEnabled = tierApp?.is_enabled || false;
                          return (
                            <div key={app.id} className="form-check" style={{ marginBottom: '2px' }}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => handleToggleApp(tier.id, app.id, isEnabled)}
                                style={{ width: '12px', height: '12px' }}
                              />
                              <label className="form-check-label" style={{ fontSize: '10px' }}>
                                {app.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-3">
                      <button className="btn btn-sm btn-info mr-1" onClick={() => handleEdit(tier)}>
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(tier.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <TierModal
          tier={editingTier}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingTier(null);
          }}
        />
      )}
    </>
  );
};

export default LicenseTiers;
